import React, { useCallback, useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import TurnstileWidget from '../components/TurnstileWidget';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

function getTurnstileHelpMessage(errorCode) {
  if (!errorCode) return 'Verification could not be completed. Please try again.';
  if (errorCode === 'unsupported') {
    return 'This browser context is not supported for verification. Disable device emulation/extensions or try a normal browser window.';
  }
  if (errorCode === 'timeout') {
    return 'Verification timed out. Please reload the challenge and try again.';
  }
  if (String(errorCode).startsWith('300') || String(errorCode).startsWith('600')) {
    return 'Verification was rejected by Cloudflare. Disable device emulation, browser extensions, VPN/proxy, then refresh and try again.';
  }
  if (errorCode === 'script-load-failed') {
    return 'Unable to load the Turnstile verification widget. Check your network, browser extensions, or site key configuration.';
  }
  if (errorCode === 'widget-render-failed') {
    return 'Turnstile failed to initialize. Refresh the page and try again, or verify your Cloudflare Turnstile settings.';
  }
  return `Verification failed (${errorCode}). Please refresh and try again.`;
}

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('sara@tutamen.com');
  const [password, setPassword] = useState('Test@123456');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileError, setTurnstileError] = useState(null);

  const handleTurnstileVerify = useCallback((token) => {
    setTurnstileToken(token);
    setTurnstileError(null);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleTurnstileError = useCallback((errorCode) => {
    setTurnstileToken(null);
    setTurnstileError(getTurnstileHelpMessage(errorCode));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // When a Turnstile site key is configured (production), require a solved token. Without a
      // site key (local dev), fall back to the bypass the backend only accepts outside production.
      const captchaToken = TURNSTILE_SITE_KEY ? turnstileToken : 'dev-bypass';
      if (TURNSTILE_SITE_KEY && !captchaToken) {
        setError(turnstileError || 'Please complete the verification challenge.');
        setLoading(false);
        return;
      }

      const response = await authAPI.login(email, password, captchaToken);

      // API returns { success, message, data }
      const data = response?.data ?? response;

      // Check if MFA required
      if (data.mfa_required) {
        setError('MFA is enabled. Please disable MFA for this demo or implement MFA flow.');
        return;
      }

      // Save token and user
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
      }
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      // Redirect to KYC
      navigate('/kyc');
    } catch (err) {
      console.error('Login failed:', err);
      setError(err.response?.data?.error || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <LoginIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography component="h1" variant="h5">
            Sign in to Tutamen
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Use your credentials to start identity verification
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleLogin} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />
          {TURNSTILE_SITE_KEY && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <TurnstileWidget
                siteKey={TURNSTILE_SITE_KEY}
                onVerify={handleTurnstileVerify}
                onExpire={handleTurnstileExpire}
                onError={handleTurnstileError}
              />
            </Box>
          )}
          {turnstileError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {turnstileError}
            </Alert>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;
